import config from 'Config/firebase';
import firebase from 'firebase/app';
import 'firebase/storage';
import 'firebase/auth';
import 'firebase/firestore';
import uuid from 'uuid';

class firebaseService {
  init() {
    if (firebase.apps.length) {
      return;
    }
    firebase.initializeApp(config);
    this.db = firebase.firestore();
    this.auth = firebase.auth();
  }

  getUserData = async uid => {
    if (!uid || !firebase.apps.length) {
      return;
    }

    try {
      const snapshotData = await this.db.collection('users').doc(uid).get();
      return snapshotData.data();
    } catch (err) {
      throw new Error('Error logging in: ', err);
    }
  };

  // This will now only work from an owner
  getItem = async id => {
    if (!firebase.apps.length) {
      return;
    }

    try {
      const snapshotData = await this.db.collection('items').doc(id).get();
      return snapshotData.data();
    } catch (err) {
      throw new Error('Error getting product from firebase: ', err);
    }
  };

  deleteItem = async itemId => {
    if (!firebase.apps.length) {
      return;
    }

    try {
      const deletedData = await this.db.collection('items').doc(itemId).delete();
    } catch (err) {
      throw new Error('Error deleting product from firebase: ', err);
    }
  };

  updateItem = async item => {
    if (!firebase.apps.length) {
      return;
    }

    try {
      const updateRes = await this.db.collection('items').doc(item.itemId).update(item);
      return updateRes;
    } catch (err) {
      throw new Error('Error updating product in database: ', err);
    }
  };

  registerProduct = async regData => {
    console.log('reg in firebase services is :: ', regData);
    const _regData = {
      ...regData,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    console.log('adding:: -> ', regData);
    try {
      await this.db.collection('registrations').add(_regData);
      return true;
    } catch (err) {
      throw new Error('Error registering product in database: ', err);
    }
  };

  getRegistrationCode = async regCodeId => {
    if (!firebase.apps.length) {
      return;
    }

    return firebase
      .auth()
      .currentUser.getIdToken()
      .then(token => {
        return fetch(`${config.functionsURL}/getRegCode`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ regCodeId }),
        });
      })
      .then(response => {
        if (response.status != 200) {
          throw Error('Error getting reg code.');
        }
        return response.json();
      })
      .catch(error => {
        console.warn('Error on server getting reg code: ', error);
        throw Error(error);
      });
  };

  getCustomerData = async (regCodeId, page, limit) => {
    if (!firebase.apps.length) {
      return;
    }
    let snapshotData;
    console.log('page and limit are :: ', page, limit, (page - 1) * limit, this.auth.currentUser.uid, regCodeId);
    try {
      // Todo add filters
      if (regCodeId) {
        snapshotData = await this.db
          .collection('registrations')
          .where('formOwner', '==', this.auth.currentUser.uid)
          .where('regCodeId', '==', regCodeId)
          .get();
      } else {
        console.log('getting all users');
        snapshotData = await this.db
          .collection('registrations')
          .where('formOwner', '==', this.auth.currentUser.uid)
          .get();
      }

      // Compound queries appear to be impossible with firestore :(
      //.where('regCodeId', '==', regCodeId)
      // .startAfter((page - 1) * limit)
      // .limit(limit)
      // .where('formOwner', '==', this.auth.currentUser.uid)
      //   .where('regCodeId', '==', regCodeId)
      // .orderBy('createdAt')

      return snapshotData.docs.map(doc => doc.data());
    } catch (err) {
      throw new Error('Error getting customer registrations: ', err);
    }
  };

  getUserRegCodes = async regCodeId => {
    if (!firebase.apps.length) {
      return;
    }
    try {
      const snapshotData = await this.db.collection('regCodes').where('ownerId', '==', this.auth.currentUser.uid).get();
      return snapshotData.docs.map(doc => doc.data());
    } catch (err) {
      throw new Error('Error getting user reg codes: ', err);
    }
  };

  createRegistrationCode = async regCodeData => {
    if (!firebase.apps.length) {
      return;
    }

    return firebase
      .auth()
      .currentUser.getIdToken()
      .then(token => {
        return fetch(`${config.functionsURL}/createRegCode`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(regCodeData),
        });
      })
      .then(response => {
        if (response.status != 200) {
          throw Error('Error creating reg code.');
        }
        return response.json();
      })
      .catch(error => {
        console.warn('Error on server creating reg code: ', error);
        throw Error(error);
      });
  };

  updateRegistrationCode = async formData => {
    if (!firebase.apps.length) {
      return;
    }

    let _strippedInfo = Object.assign({}, formData);
    // add created at
    delete _strippedInfo.template;

    try {
      let updateRes = await this.db
        .collection('regCodes')
        .doc(formData.regCodeId)
        .update(
          {
            ...formData,
          },
          { merge: true },
        );
      return updateRes.regCodeId;
    } catch (err) {
      throw new Error('Error updating registration code in database: ', err);
    }
  };

  getUserItems = async () => {
    if (!firebase.apps.length || !firebase.auth().currentUser) {
      return;
    }

    try {
      const snapshotData = await this.db.collection('items').where('ownerId', '==', this.auth.currentUser.uid).get();
      return snapshotData.docs.map(doc => doc.data());
    } catch (err) {
      throw new Error('Error getting user items from firebase: ', err);
    }
  };

  // currently this is just getting all the products
  // we'll probably want to do some kind of filter in the
  // future
  getProductData = async () => {
    if (!firebase.apps.length) {
      return;
    }

    try {
      const snapshotData = await this.db.collection('products').get();
      return snapshotData.docs.map(doc => doc.data());
    } catch (err) {
      throw new Error('Error getting products from firebase: ', err);
    }
  };

  getPlanData = async () => {
    if (!firebase.apps.length) {
      return;
    }

    try {
      const snapshotData = await this.db
        .collection('plans')
        .where('type', '==', 'registration')
        .get()
        .catch(err => console.log(err));
      return snapshotData.docs.map(doc => doc.data());
    } catch (err) {
      throw new Error('Error getting plans from firebase: ', err);
    }
  };

  createUserData = user => {
    if (!firebase.apps.length) {
      return;
    }
    const tmpUser = { ...user, password: null, createdAt: firebase.firestore.FieldValue.serverTimestamp() };
    this.db.collection('users').doc(tmpUser.uid).set(tmpUser);
  };

  updateUserData = user => {
    if (!firebase.apps.length) {
    }
  };

  onAuthStateChanged = callback => {
    if (!this.auth) {
      return;
    }
    this.auth.onAuthStateChanged(callback);
  };

  signOut = async () => {
    if (!this.auth) {
      return;
    }
    this.auth.signOut();
  };

  resetPassword = async email => {
    return this.auth.sendPasswordResetEmail(email);
  };

  uploadImages = async ({ images, userId, isThumbs }) => {
    if (!firebase.apps.length) {
      return;
    }
    const storageRef = firebase.storage().ref();
    const promisesArr = [];
    const downloadLinks = [];

    images.forEach(img => {
      let imageId = isThumbs ? `${uuid.v4()}_thumb` : uuid.v4();
      let imageRef = storageRef.child(`/images/${userId}/${imageId}`);
      promisesArr.push(
        imageRef
          .putString(img, 'data_url')
          .then(imgRes => {
            return imgRes.ref.getDownloadURL();
          })
          .then(downloadURL => {
            downloadLinks.push(downloadURL);
          }),
      );
    });

    return Promise.all(promisesArr).then(promisesRes => {
      // promisesRes alwars returns an array of null
      return downloadLinks;
    });
  };
}

const instance = new firebaseService();

export default instance;
